import axios from './upstreamAwareAxios';
import { getAuth } from '../providers/AppAuthProvider';
import { baseUrl, getAuthHeaders as getFetchAuthHeaders } from '../providers/AppDataProvider';
import { MoneyRequest, MoneyRequestPage, moneyCall } from './moneyRequestApi';

async function getAuthHeaders() {
  const { accessToken } = (await getAuth()) || {};
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

const prefix = `${baseUrl}/blind-box`;

function unwrapOrThrow<T>(data: any): T {
  // 后端部分接口在 HTTP 200 下仍可能返回 GraphQL 风格 errors
  if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
    const err = data.errors[0];
    throw new Error(err?.message || 'Error');
  }
  return data as T;
}

// =============================================================================
// Types（与 admin-service blind-box 模块字段对齐，按业务域分组）
// =============================================================================

/** 总览（与 Loyalty `BBAdminDashboard` / admin-service `bbAdminDashboard` 查询字段一致） */
export interface BbDashboard {
  totalUsers: number;
  totalBoxOpens: number;
  totalWinsPending: number;
  totalWithdrawals: number;
  activityStatus: string;
  /** 当前活动周期结束时间，无周期时为 null */
  activityEndsAt?: string | null;
  /** UTC 今日开盒次数合计 */
  todayBoxOpens?: number;
  /** UTC 昨日开盒次数合计 */
  yesterdayBoxOpens?: number;
  /** UTC 今日有开盒的去重用户数 */
  todayActiveUsers?: number;
  /** UTC 今日已发放 USDC 奖励合计（Decimal，JSON 多为 string） */
  todayRewardsDistributedUSDC?: string | number;
  /** 历史累计已发放 USDC 奖励（Decimal） */
  totalRewardsDistributedUSDC?: string | number;
}

/** 活动配置 · 活动基础 */
export interface BbActivityMeta {
  title: string;
  subtitle: string;
  ctaText: string;
  navbarTitle: string;
  backButtonText: string;
}

export interface BbFeatureFlag {
  key: string;
  value: boolean;
  description?: string;
  updatedAt?: string;
}

export interface BbConfigRow {
  key: string;
  value: string;
  description?: string;
  updatedAt?: string;
}

/** 活动配置 · 幸运转盘 / 盲盒概率 */
export interface BbReward {
  id: number;
  sourceModule: string;
  rewardCategory: string;
  tokenType?: string;
  name: string;
  description?: string;
  amount: string;
  totalPool: number;
  baseProbability: string;

  isActive: boolean;
  displayOrder: number;
  iconUrl?: string;
  isRare: boolean;
  /** 转盘扇区展示（WHEEL） */
  expression?: string;
}

export interface BbPoolStock {
  rewardId: number;
  rewardName: string;
  remaining: number;
  distributed: number;
  totalPool: number;
}

export interface BbValidateProbabilities {
  sourceModule: string;
  total: string;
  isValid: boolean;
  items: { rewardId: number; rewardName: string; probability: string }[];
}

/** 活动配置 · 交易量阶梯（任务阶梯） */
export interface BbTaskLadder {
  id: number;
  taskId: number;
  volumeThreshold: string;
  rewardBox: number;
  isTopTier: boolean;
  displayOrder: number;
}

export interface BbTask {
  id: number;
  category: string;
  resetType: string;
  slug: string;
  name: string;
  description?: string;
  rewardXp: number;
  rewardSpin: number;
  rewardBox: number;
  isActive: boolean;
  displayOrder: number;
  externalLink?: string;
  ladders: BbTaskLadder[];
}

/** 成长 & 彩票 · 等级系统 */
export interface BbLevel {
  id: number;
  name: string;
  requiredXp: number;
  lotteryWeight: number;
  displayOrder: number;
}

export interface BbXpRule {
  action: string;
  xpAmount: number;
  description?: string;
}

/** 成长 & 彩票 · 节日彩票 */
export interface BbSeason {
  id: string;
  seasonNumber: string;
  name: string;
  description?: string;
  startTime: string;
  endTime: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BbSeasonsResponse {
  seasons: BbSeason[];
  total: number;
}

export interface BbPrizeTier {
  id?: string;
  tierName: string;
  winnersCount: number;
  rewardAmount: string;
  /** 中奖号码列表（可能为空） */
  winningNumbers?: string[];
  winnerSlots?: { position: number; number: string; txHash?: string | null }[];
  displayOrder: number;
}

/** 与后端 BBAdminLotteryIssueI18n 一致；旧数据可能为 null，展示需回退到 name */
export type BbLotteryIssueNameI18nKey = 'vi' | 'en' | 'zhHans' | 'zhHant' | 'ja' | 'ko' | 'ar';

export type BbLotteryIssueNameI18n = Partial<Record<BbLotteryIssueNameI18nKey, string | null>>;

export const BB_LOTTERY_ISSUE_NAME_I18N_KEYS: readonly BbLotteryIssueNameI18nKey[] = [
  'en',
  'zhHans',
  'zhHant',
  'vi',
  'ja',
  'ko',
  'ar',
] as const;

/** 表单用：把 API 返回的 nameI18n（可 null / 部分）展开为各 locale 字符串 */
export function mergeLotteryIssueNameI18nFromApi(
  i18n?: BbLotteryIssueNameI18n | null
): Record<BbLotteryIssueNameI18nKey, string> {
  const base = Object.fromEntries(BB_LOTTERY_ISSUE_NAME_I18N_KEYS.map((k) => [k, ''])) as Record<
    BbLotteryIssueNameI18nKey,
    string
  >;
  if (!i18n) return base;
  return BB_LOTTERY_ISSUE_NAME_I18N_KEYS.reduce<Record<BbLotteryIssueNameI18nKey, string>>(
    (acc, k) => {
      const v = i18n[k];
      acc[k] = v != null && String(v).length ? String(v) : '';
      return acc;
    },
    { ...base }
  );
}

/** 仅提交非空文案的 locale（部分更新）；全无则返回 undefined 不传字段 */
export function buildLotteryIssueNameI18nPayload(
  i18n: Record<BbLotteryIssueNameI18nKey, string> | BbLotteryIssueNameI18n | null | undefined
): Record<string, string> | undefined {
  if (!i18n) return undefined;
  const out = BB_LOTTERY_ISSUE_NAME_I18N_KEYS.reduce<Record<string, string>>((acc, k) => {
    const raw = (i18n as Record<string, unknown>)[k];
    const s = raw !== undefined && raw !== null ? String(raw).trim() : '';
    if (s !== '') acc[k] = s;
    return acc;
  }, {});
  return Object.keys(out).length ? out : undefined;
}

/** 展示用：无多语言或某 locale 为空时回退到 name */
export function bbLotteryIssueLocalizedName(
  issue: Pick<BbLotteryIssue, 'name' | 'nameI18n'>,
  locale: BbLotteryIssueNameI18nKey
): string {
  const v = issue.nameI18n?.[locale];
  if (v != null && String(v).trim() !== '') return String(v).trim();
  return issue.name ?? '';
}

/** 列表/下拉：优先默认 name，否则任一有值的多语言，否则仅期号 */
export function bbLotteryIssueListLabel(
  issue: Pick<BbLotteryIssue, 'issueNumber' | 'name' | 'nameI18n'>
): string {
  const num = String(issue.issueNumber ?? '').trim();
  const primary = String(issue.name ?? '').trim();
  if (primary) return `${num} · ${primary}`;
  const tryKeys: BbLotteryIssueNameI18nKey[] = ['en', 'zhHans', 'zhHant', 'vi', 'ja', 'ko', 'ar'];
  const i18nLabel = tryKeys
    .map((k) => {
      const v = issue.nameI18n?.[k];
      return v != null ? String(v).trim() : '';
    })
    .find((s) => s.length > 0);
  if (i18nLabel) return `${num} · ${i18nLabel}`;
  return num || '—';
}

export interface BbLotteryIssue {
  id: number;
  seasonId: string;
  issueNumber: string;
  name: string;
  nameI18n?: BbLotteryIssueNameI18n | null;
  /** 期次开始时间（ISO 等后端 Time 序列化格式） */
  startTime?: string;
  endTime: string;
  status: string;
  /** 彩票号码位数，1–10 */
  ticketDigitCount?: number;
  prizeTiers: BbPrizeTier[];
}

export interface BbLotteryTicketTier {
  threshold: string;
  ticketCount: number;
}

export interface BbLotteryTicketLadder {
  boxOpenTickets: number;
  volumeTiers: BbLotteryTicketTier[];
  /** 按 predict 交易量阈值升序的两档赠券 */
  predictVolumeTiers?: BbLotteryTicketTier[];
  /** Prediction Leverage（loyalty: lottery_ticket_vol_tiers_leverage） */
  leverageVolumeTiers?: BbLotteryTicketTier[];
  /** Tap Grid（loyalty: lottery_ticket_vol_tiers_tap_grid） */
  tapGridVolumeTiers?: BbLotteryTicketTier[];
}

/** 四组阶梯的字段名，UI 遍历渲染用；写入语义各组一致（不传=不变 / 非空=全量替换 / []=清空） */
export const BB_LOTTERY_TICKET_TIER_GROUPS = [
  'volumeTiers',
  'predictVolumeTiers',
  'leverageVolumeTiers',
  'tapGridVolumeTiers',
] as const;

export type BbLotteryTicketTierGroup = (typeof BB_LOTTERY_TICKET_TIER_GROUPS)[number];

/** 成长 & 彩票 · 各等级概率 */
export interface BbLevelProbabilityCell {
  levelId: number;
  rewardId: number;
  rewardName: string;
  sourceModule: string;
  probability: string;
  /** 仅 isActive=true 的奖励参与盲盒等级矩阵展示 */
  isActive?: boolean;
}

/** 内容 · 最新中奖滚动 */
export interface BbWinnersTickerRow {
  id: number;
  walletAddress: string;
  rewardDesc: string;
  timeDesc: string;
  levelId?: number;
  isActive: boolean;
  displayOrder: number;
}

/** 内容 · 中奖记录 */
export interface BbWinRecord {
  id: string;
  userId: string;
  walletAddress?: string;
  source: string;
  rewardName: string;
  rewardCategory: string;
  tokenType?: string;
  amount: string;
  status: string;
  reviewNote?: string;
  createdAt: string;
  /** 彩票来源时为期号，如 #2026-001 */
  issueNumber?: string;
}

export interface BbWinRecordsPage {
  entries: BbWinRecord[];
  total: number;
}

export interface BbPagedResponse<T> {
  total: number;
  hasMore: boolean;
  entries: T[];
}

export interface BbOpenSessionEntry {
  id: string;
  userId: string;
  walletAddress: string;
  seasonId?: string;
  openCount: number;
  totalXp: number;
  status: string;
  createdAt: string;
}

export interface BbTaskClaimEntry {
  id: string;
  userId: string;
  taskId: number;
  taskName: string;
  taskSlug: string;
  status: string;
  progress?: string | number;
  claimedAt?: string | null;
  resetDate?: string | null;
  createdAt: string;
}

export interface BbLotteryTicketEntry {
  id: string;
  userId: string;
  issueId: number;
  issueNumber: string;
  ticketNumber: string;
  source: string;
  /** 后端可能返回 ISO string 或毫秒时间戳 */
  createdAt: string | number;
}

export interface BbUserXpHistoryEntry {
  id: string;
  userId: string;
  source: string;
  amount: number;
  note?: string | null;
  /** 后端可能返回 ISO string 或毫秒时间戳 */
  createdAt: string | number;
}

export interface BbSpinBoxGrantHistoryEntry {
  id: string;
  userId: string;
  source: string;
  sourceRef?: string | null;
  spinsGranted: number;
  boxesGranted: number;
  /** 后端可能返回 ISO string 或毫秒时间戳 */
  createdAt: string | number;
}

// =============================================================================
// Reward feed（统一流水）
// =============================================================================

export interface BbRewardFeedEntry {
  id: string;
  userId: string;
  walletAddress?: string | null;
  eventType: 'GRANT' | 'WIN' | 'XP' | 'LOTTERY_TICKET' | string;
  spinsGranted?: number | null;
  boxesGranted?: number | null;
  rewardCategory?: string | null;
  rewardName?: string | null;
  tokenType?: string | null;
  xpAmount?: number | null;
  ticketNumber?: string | null;
  issueId?: number | null;
  issueNumber?: string | null;
  seasonId?: string | null;
  createdAt?: string | number | null;

  // new unified fields
  taskSlug?: string | null;
  status?: string | null;
  amount?: string | number | null;
  source?: string | null;
}

export interface BbRewardFeedResponse extends BbPagedResponse<BbRewardFeedEntry> {
  userBalance?: {
    userId: string;
    walletAddress?: string | null;
    wheelSpins?: number | null;
    dailyBoxChances?: number | null;
    totalXp?: number | null;
    usdcBalance?: string | number | null;
  } | null;
}

/** POST /blind-box/reward-feed-usdc（WIN + CASH_USDC，字段与 BbAdminUSDCRewardFeed 一致） */
export interface BbUsdcRewardFeedEntry {
  id: string;
  userId: string;
  walletAddress?: string | null;
  source?: string | null;
  status?: string | null;
  rewardName?: string | null;
  amount?: string | number | null;
  tokenType?: string | null;
  issueId?: number | null;
  issueNumber?: string | null;
  seasonId?: string | null;
  createdAt?: string | number | null;
}

export interface BbUsdcRewardFeedResponse extends BbPagedResponse<BbUsdcRewardFeedEntry> {
  userBalance?: {
    walletAddress?: string | null;
    usdcBalance?: string | number | null;
  } | null;
}

export interface BbUserRewardSummaryByTask {
  taskSlug: string;
  earned: number;
}

/** 来源 WIN 的中奖奖励拆分（如「再转一次」发放的 Spin/Box） */
export interface BbUserRewardSummaryByWin {
  rewardName: string;
  earned: number;
}

export interface BbUserRewardSummaryBySource {
  source: string;
  totalXp: number;
}

export interface BbUserRewardSummaryTicketBySource {
  source: string;
  count: number;
}

export interface BbUserRewardSummaryWinByCategory {
  rewardCategory: string;
  count: number;
  totalAmount: string | number;
}

export interface BbUserRewardSummaryWinsBySource {
  source: string;
  totalWins: number;
  byCategory: BbUserRewardSummaryWinByCategory[];
}

export interface BbUserRewardSummary {
  userId: string;
  walletAddress?: string | null;
  spins: {
    totalEarned: number;
    pending: number;
    distributed: number;
    byTask: BbUserRewardSummaryByTask[];
    byWin?: BbUserRewardSummaryByWin[];
  };
  boxes: {
    totalEarned: number;
    pending: number;
    distributed: number;
    byTask: BbUserRewardSummaryByTask[];
    byWin?: BbUserRewardSummaryByWin[];
  };
  xp: {
    totalEarned: number;
    bySource: BbUserRewardSummaryBySource[];
  };
  lotteryTickets: {
    totalTickets: number;
    bySource: BbUserRewardSummaryTicketBySource[];
  };
  wins?: {
    totalWins: number;
    bySource: BbUserRewardSummaryWinsBySource[];
  };
}

// =============================================================================
// 手动发放盲盒（manual-distributions）
// =============================================================================

/** NORMAL=随机奖励盒（发放时从概率池预分配）；DESIGNATED_AMOUNT=固定金额盒（开出恰好 amount USDC） */
export type BbBoxType = 'NORMAL' | 'DESIGNATED_AMOUNT';

export const BB_BOX_TYPES: BbBoxType[] = ['NORMAL', 'DESIGNATED_AMOUNT'];

/** 发放原因（后端 @IsIn 约束） */
export type BbGrantReason =
  | 'ACTIVITY_REWARD'
  | 'CS_REISSUE'
  | 'RISK_COMPENSATION'
  | 'MANUAL_OPERATION'
  | 'OTHER';

export const BB_GRANT_REASONS: BbGrantReason[] = [
  'ACTIVITY_REWARD',
  'CS_REISSUE',
  'RISK_COMPENSATION',
  'MANUAL_OPERATION',
  'OTHER',
];

/** DESIGNATED_AMOUNT 单盒金额默认上限（USDC），后端亦会二次校验 */
export const BB_DESIGNATED_AMOUNT_CAP = 10000;

/** 单条发放入参（GrantBoxDto）。userId / walletAddress 至少一项；两者都传时 userId 优先。 */
export interface BbGrantBoxInput {
  userId?: string;
  walletAddress?: string;
  boxType: BbBoxType;
  /** NORMAL: 1..100；DESIGNATED_AMOUNT: 必须为 1 */
  quantity: number;
  /** Decimal 字符串；仅 DESIGNATED_AMOUNT 传，NORMAL 必须省略 */
  amount?: string;
  reason: BbGrantReason;
  notes?: string;
  operator: string;
}

/** 发放审计记录（BBAdminManualDistribution） */
export interface BbManualDistribution {
  id: string;
  /** 批量发放时归组，单条发放为 null */
  batchId?: string | null;
  userId?: string | null;
  /** 规范化小写；按裸 userId 发放且无绑定钱包时为 null */
  walletAddress?: string | null;
  boxType: BbBoxType;
  quantity: number;
  /** NORMAL 为 null */
  designatedAmount?: string | null;
  /** 该记录已被用户开启的盒数；== quantity 表示已全部开启 */
  openedCount: number;
  reason: BbGrantReason | string;
  notes?: string | null;
  operator: string;
  createdAt: string;
  /** 被管理员一键开启后才有值（见 force-open），否则为 null */
  forceOpenedAt?: string | null;
  forceOpenedBy?: string | null;
  forceOpenedCount?: number | null;
  /** Set once the record has been revoked (see revoke); null otherwise */
  revokedAt?: string | null;
  revokedBy?: string | null;
  revokeReason?: string | null;
  /** Unopened boxes cancelled by the revoke */
  revokedBoxCount?: number | null;
  /** USDC clawed back from the user's available balance so far (Decimal) */
  clawedBackAmount?: string | number | null;
  /** USDC still owed after clawback (balance never goes below zero); > 0 = re-run revoke later */
  clawbackShortfall?: string | number | null;
}

/** 批量发放行级错误（BBAdminBatchGrantBoxError） */
export interface BbBatchGrantError {
  /** 提交数组中的 0-based 下标 */
  index: number;
  code: string;
  message: string;
}

/** 批量发放结果（BBAdminBatchGrantBoxResult）：success=false 时未持久化任何记录 */
export interface BbBatchGrantResult {
  success: boolean;
  records: BbManualDistribution[];
  errors: BbBatchGrantError[];
}

export interface BbManualDistributionQuery {
  /** 仅取该记录（UUID）。用于逐盒明细的行内展开；无效 UUID 报 INVALID_DISTRIBUTION_ID，有效但不存在返回空页 */
  distributionId?: string;
  userId?: string;
  walletAddress?: string;
  boxType?: BbBoxType;
  reason?: BbGrantReason;
  operator?: string;
  fromDate?: string;
  toDate?: string;
  /** true = revoked records only; false = not revoked only; omitted = all */
  revoked?: boolean;
  /** 1-based；< 1 归一化为 1 */
  page?: number;
  /** <= 0 归一化为 20；> 200 归一化为 200 */
  pageSize?: number;
}

/** 分页响应（BBAdminManualDistributionPage）；page/pageSize 为服务端归一化后的权威值 */
export interface BbManualDistributionPage {
  total: number;
  page: number;
  pageSize: number;
  /** 按 createdAt 倒序（newest first） */
  items: BbManualDistribution[];
}

/** POST /blind-box/manual-distributions/grant — 单条发放（blind-box:create） */
export async function bbGrantBox(body: BbGrantBoxInput): Promise<BbManualDistribution> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/manual-distributions/grant`, body, {
    headers: h,
  });
  return unwrapOrThrow<BbManualDistribution>(data);
}

/** POST /blind-box/manual-distributions/batch-grant — 批量发放（原子；1..1000 行） */
export async function bbBatchGrantBox(
  inputs: BbGrantBoxInput[]
): Promise<BbBatchGrantResult> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/manual-distributions/batch-grant`,
    { inputs },
    { headers: h }
  );
  return unwrapOrThrow<BbBatchGrantResult>(data);
}

/** POST /blind-box/manual-distributions — 发放历史（筛选 + 分页；blind-box:read） */
export async function bbGetManualDistributions(
  query: BbManualDistributionQuery = {}
): Promise<BbManualDistributionPage> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/manual-distributions`, query, {
    headers: h,
  });
  return unwrapOrThrow<BbManualDistributionPage>(data);
}

/** 手动发放看板统计（BBAdminManualDistributionStats） */
export interface BbManualDistributionStats {
  /** 区间内 SUM(quantity) */
  totalBoxes: number;
  /** 今日 UTC 起 SUM(quantity)——始终为「今日」，忽略 fromDate/toDate */
  todayBoxes: number;
  /** 区间内发放记录数 */
  totalRecords: number;
  /** 区间内 DESIGNATED_AMOUNT 盒的 USDC 合计（Decimal） */
  designatedAmountTotal: string | number;
  /** 区间内 NORMAL 手动盒内预置 CASH_USDC 奖励的 USDC 合计（Decimal；不含 XP/Spin 等，过期盒不计） */
  normalCashAmountTotal: string | number;
  /** 区间内已开启的手动盒数量 */
  openedBoxes: number;
}

/** GET /blind-box/manual-distributions/stats — 看板统计（blind-box:read）
 *  fromDate/toDate 作用于除 todayBoxes 外的所有字段；todayBoxes 恒为今日 */
export async function bbGetManualDistributionStats(params?: {
  fromDate?: string;
  toDate?: string;
}): Promise<BbManualDistributionStats> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/manual-distributions/stats`, {
    headers: h,
    params: {
      ...(params?.fromDate ? { fromDate: params.fromDate } : {}),
      ...(params?.toDate ? { toDate: params.toDate } : {}),
    },
  });
  return unwrapOrThrow<BbManualDistributionStats>(data);
}

/** 单盒开启状态（BBAdminManualBoxStatus） */
export type BbManualBoxStatus = 'PENDING' | 'CONSUMED' | 'EXPIRED' | 'REVOKED';

/** 逐盒明细（BBAdminManualDistributionBox）：父发放记录字段 + 每盒预置奖励，1 行 = 1 个盒，用于 CSV 导出 */
export interface BbManualDistributionBox {
  /** 父 manual_distributions 记录 id */
  distributionId: string;
  batchId?: string | null;
  userId: string;
  walletAddress?: string | null;
  boxType: BbBoxType;
  reason: BbGrantReason | string;
  notes?: string | null;
  operator: string;
  /** 发放时间（父记录 createdAt） */
  grantedAt: string;
  /** 该盒对应 pending_rewards 行 id */
  pendingId: string;
  status: BbManualBoxStatus | string;
  /** 开启时间；未开启为 null */
  openedAt?: string | null;
  /** 发放时预置，PENDING/EXPIRED 也会返回 */
  rewardCategory: string;
  rewardName: string;
  /** 固定金额（designated/expression）时为结算金额，否则为奖励基础金额（Decimal） */
  rewardAmount: string | number;
}

export interface BbManualDistributionBoxPage {
  items: BbManualDistributionBox[];
  /** 匹配的「盒」总数（非记录数）——ceil(total/pageSize) 页可导出完整明细 */
  total: number;
  page: number;
  pageSize: number;
}

/** POST /blind-box/manual-distributions/boxes — 逐盒明细（筛选 + 分页；blind-box:read）
 *  与历史查询同一套筛选/分页语义；total 计的是盒数，pageSize 上限 200 */
export async function bbGetManualDistributionBoxes(
  query: BbManualDistributionQuery = {}
): Promise<BbManualDistributionBoxPage> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/manual-distributions/boxes`, query, {
    headers: h,
  });
  return unwrapOrThrow<BbManualDistributionBoxPage>(data);
}

/** 一键开启的单盒结果（BBAdminForceOpenBox） */
export interface BbForceOpenBox {
  /** win_history 行 id（持久的开启记录） */
  winId: string;
  rewardCategory: string;
  rewardName: string;
  /** 实际发放金额（Decimal） */
  amount: string | number;
}

/** 一键开启结果（BBAdminForceOpenResult） */
export interface BbForceOpenResult {
  distributionId: string;
  /** 本次调用开启的盒数 */
  openedCount: number;
  /** 调用前用户已开启的盒数 */
  alreadyOpenedCount: number;
  forceOpenedAt: string;
  results: BbForceOpenBox[];
}

/** POST /blind-box/manual-distributions/:distributionId/force-open — 代开该记录剩余未开盒（blind-box:update）
 *  一记录仅可一次；再次调用返回 ALREADY_FORCE_OPENED */
export async function bbForceOpenManualDistribution(
  distributionId: string,
  operator: string
): Promise<BbForceOpenResult> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/manual-distributions/${distributionId}/force-open`,
    { operator },
    { headers: h }
  );
  return unwrapOrThrow<BbForceOpenResult>(data);
}

/** Max distribution ids per revoke call (backend DTO limit). */
export const BB_REVOKE_MAX_IDS = 500;
/** Max revoke reason length (backend DTO limit). */
export const BB_REVOKE_REASON_MAX = 255;

/** Per-record revoke result */
export interface BbRevokeDistributionResult {
  distributionId: string;
  success: boolean;
  errorCode?: string | null;
  errorMessage?: string | null;
  /** The record was already revoked before this call (this call may still collect shortfall) */
  alreadyRevoked: boolean;
  /** Unopened boxes cancelled by this call */
  boxesRevoked: number;
  /** USDC deducted from the user's available balance by this call (Decimal) */
  clawedBackNow: string | number;
  /** Updated record (carries clawedBackAmount / clawbackShortfall after this call) */
  record?: BbManualDistribution | null;
}

export interface BbRevokeDistributionsResult {
  successCount: number;
  failureCount: number;
  results: BbRevokeDistributionResult[];
}

/**
 * POST /blind-box/manual-distributions/revoke — revoke manual distributions
 * (permission `bb-manual-distribution:revoke`). Cancels the record's unopened boxes and claws back
 * USDC already opened from the user's available balance (never below zero); any remainder is kept
 * as `clawbackShortfall` and is collected by calling revoke again on the same record later (e.g.
 * after rejecting the user's pending withdrawal). Operator is taken from the JWT server-side.
 * distributionIds: 1..500; reason: 1..255 chars.
 */
export async function bbRevokeManualDistributions(body: {
  distributionIds: string[];
  reason: string;
}): Promise<BbRevokeDistributionsResult> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/manual-distributions/revoke`, body, {
    headers: h,
  });
  return unwrapOrThrow<BbRevokeDistributionsResult>(data);
}

/** 提现管理 */
export interface BbWithdrawalEntry {
  id: string;
  userId: string;
  tokenType: string;
  amount: string;
  walletAddress?: string;
  status: string;
  requestedAt: string;
  /** User is currently banned from Blind Box — the backend refuses to approve their withdrawals */
  isBanned?: boolean;
}

export interface BbWithdrawalsPage {
  entries: BbWithdrawalEntry[];
  total: number;
}

/** 社交关注（Twitter / Telegram 任务核验） */
export enum BbSocialFollowType {
  TWITTER = 'TWITTER',
  TWITTER_ZH = 'TWITTER_ZH',
  TELEGRAM = 'TELEGRAM',
}

export interface BbAdminSocialFollowEntry {
  userId: string;
  socialUsername: string;
  socialType: BbSocialFollowType | string;
  socialTypeLabel: string;
  isFollowing: boolean;
  followCheckedAt?: string | null;
  boundAt: string;
}

export interface BbAdminSocialFollowListResponse {
  total: number;
  entries: BbAdminSocialFollowEntry[];
}

// =============================================================================
// 1. 总览
// =============================================================================

export async function bbGetDashboard(): Promise<BbDashboard> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/dashboard`, { headers: h });
  return unwrapOrThrow<BbDashboard>(data);
}

// =============================================================================
// 2. 活动配置
// =============================================================================

// --- 活动基础设置（文案 / 功能开关 / 运行时配置） ---

export async function bbGetActivityMeta(): Promise<BbActivityMeta> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/activity-meta`, { headers: h });
  return unwrapOrThrow<BbActivityMeta>(data);
}

export async function bbSetActivityMeta(body: Partial<BbActivityMeta>): Promise<BbActivityMeta> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/activity-meta`, body, { headers: h });
  return unwrapOrThrow<BbActivityMeta>(data);
}

export async function bbGetFeatureFlags(): Promise<BbFeatureFlag[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/feature-flags`, { headers: h });
  return unwrapOrThrow<BbFeatureFlag[]>(data);
}

export async function bbSetFeatureFlag(key: string, value: boolean): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/feature-flags`, { key, value }, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

export async function bbGetConfigs(): Promise<BbConfigRow[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/configs`, { headers: h });
  return unwrapOrThrow<BbConfigRow[]>(data);
}

export async function bbSetConfig(key: string, value: string): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/configs`, { key, value }, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

export type BbRewardPayoutChannel = 'HYPERLIQUID_USDC' | 'POLYGON_USDC' | 'POLYGON_PUSD';

export const BB_REWARD_PAYOUT_CHANNELS: BbRewardPayoutChannel[] = [
  'HYPERLIQUID_USDC',
  'POLYGON_USDC',
  'POLYGON_PUSD',
];

export async function bbGetRewardPayoutChannel(): Promise<BbRewardPayoutChannel> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/reward-payout-channel`, { headers: h });
  return unwrapOrThrow<BbRewardPayoutChannel>(data);
}

export async function bbSetRewardPayoutChannel(channel: BbRewardPayoutChannel): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/reward-payout-channel`, { channel }, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

// --- 幸运转盘 / 盲盒概率（奖励与奖池） ---

export async function bbGetRewards(params?: {
  sourceModule?: 'WHEEL' | 'BOX';
}): Promise<BbReward[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/rewards`, {
    headers: h,
    params: params?.sourceModule ? { sourceModule: params.sourceModule } : undefined,
  });
  return unwrapOrThrow<BbReward[]>(data);
}

export async function bbCreateReward(body: Record<string, unknown>): Promise<BbReward> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/rewards`, body, { headers: h });
  return unwrapOrThrow<BbReward>(data);
}

export async function bbUpdateReward(id: number, body: Record<string, unknown>): Promise<BbReward> {
  const h = await getAuthHeaders();
  const { data } = await axios.put<any>(`${prefix}/rewards/${id}`, body, { headers: h });
  return unwrapOrThrow<BbReward>(data);
}

export async function bbDeleteReward(id: number): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.delete<any>(`${prefix}/rewards/${id}`, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

export async function bbRefillPool(rewardId: number, amount: number): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/rewards/${rewardId}/refill-pool`,
    { amount },
    { headers: h }
  );
  return unwrapOrThrow<boolean>(data);
}

export async function bbGetPoolStocks(params?: {
  sourceModule?: 'WHEEL' | 'BOX';
}): Promise<BbPoolStock[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/pool-stocks`, {
    headers: h,
    params: params?.sourceModule ? { sourceModule: params.sourceModule } : undefined,
  });
  return unwrapOrThrow<BbPoolStock[]>(data);
}

export async function bbValidateRewardProbabilities(
  sourceModule: string
): Promise<BbValidateProbabilities> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/validate-reward-probabilities`, {
    headers: h,
    params: { sourceModule },
  });
  return unwrapOrThrow<BbValidateProbabilities>(data);
}

// --- 交易量阶梯（任务阶梯） ---

export async function bbUpsertTaskLadder(body: Record<string, unknown>): Promise<BbTaskLadder> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/task-ladders`, body, { headers: h });
  return unwrapOrThrow<BbTaskLadder>(data);
}

export async function bbDeleteTaskLadder(id: number): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.delete<any>(`${prefix}/task-ladders/${id}`, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

// =============================================================================
// 3. 成长 & 彩票
// =============================================================================

// --- 等级系统 ---

export async function bbGetLevels(): Promise<BbLevel[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/levels`, { headers: h });
  return unwrapOrThrow<BbLevel[]>(data);
}

export async function bbUpsertLevel(body: Record<string, unknown>): Promise<BbLevel> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/levels`, body, { headers: h });
  return unwrapOrThrow<BbLevel>(data);
}

export async function bbDeleteLevel(id: number): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.delete<any>(`${prefix}/levels/${id}`, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

export async function bbGetXpRules(): Promise<BbXpRule[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/xp-rules`, { headers: h });
  return unwrapOrThrow<BbXpRule[]>(data);
}

export async function bbUpsertXpRule(body: {
  action: string;
  xpAmount: number;
}): Promise<BbXpRule> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/xp-rules`, body, { headers: h });
  return unwrapOrThrow<BbXpRule>(data);
}

// --- 各等级概率 ---

export async function bbGetLevelProbabilities(levelId?: number): Promise<BbLevelProbabilityCell[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/level-probabilities`, {
    headers: h,
    params: levelId !== undefined ? { levelId } : {},
  });
  return unwrapOrThrow<BbLevelProbabilityCell[]>(data);
}

export async function bbSetLevelProbability(body: {
  rewardId: number;
  levelId: number;
  probability: string;
}): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/level-probability`, body, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

// --- 节日彩票 ---

export async function bbGetSeasons(limit?: number, offset?: number): Promise<BbSeasonsResponse> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/seasons`, {
    headers: h,
    params: { limit, offset },
  });
  return unwrapOrThrow<BbSeasonsResponse>(data);
}

export async function bbCreateSeason(body: Record<string, unknown>): Promise<BbSeason> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/seasons`, body, { headers: h });
  return unwrapOrThrow<BbSeason>(data);
}

export async function bbUpdateSeason(body: Record<string, unknown>): Promise<BbSeason> {
  const h = await getAuthHeaders();
  const { data } = await axios.put<any>(`${prefix}/seasons`, body, { headers: h });
  return unwrapOrThrow<BbSeason>(data);
}

export async function bbGetLotteryIssues(seasonId: string): Promise<BbLotteryIssue[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/seasons/${seasonId}/lottery-issues`, {
    headers: h,
  });
  return unwrapOrThrow<BbLotteryIssue[]>(data);
}

export async function bbCreateLotteryIssue(body: Record<string, unknown>): Promise<BbLotteryIssue> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/lottery-issues`, body, { headers: h });
  return unwrapOrThrow<BbLotteryIssue>(data);
}

export async function bbUpdateLotteryIssue(
  id: number,
  body: Record<string, unknown>
): Promise<BbLotteryIssue> {
  const h = await getAuthHeaders();
  const { data } = await axios.put<any>(`${prefix}/lottery-issues/${id}`, body, { headers: h });
  return unwrapOrThrow<BbLotteryIssue>(data);
}

export async function bbExecuteLotteryDraw(
  issueId: number,
  fallbackSeed?: string
): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/lottery-issues/${issueId}/draw`,
    { fallbackSeed },
    { headers: h }
  );
  return unwrapOrThrow<boolean>(data);
}

export async function bbGetLotteryTicketLadder(): Promise<BbLotteryTicketLadder> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/lottery-ticket-ladder`, { headers: h });
  return unwrapOrThrow<BbLotteryTicketLadder>(data);
}

export async function bbSetLotteryTicketLadder(
  body: BbLotteryTicketLadder
): Promise<BbLotteryTicketLadder> {
  const h = await getAuthHeaders();
  // 后端语义：字段不传=保持不变；非空数组=全量替换；空数组=清空该组
  // 这里保证不会提交 threshold="" 的项（空字符串会导致后端 decimal 解析报错）；
  // 用户显式清空（传入 []）时透传空数组以触发清空，仅有非法行时则不传该字段。
  const normalizeTiers = (tiers?: BbLotteryTicketTier[]) => {
    if (tiers === undefined) return undefined;
    if (tiers.length === 0) return [];
    const clean = tiers.filter((x) => String(x?.threshold ?? '').trim() !== '');
    return clean.length ? clean : undefined;
  };

  const payload: Record<string, unknown> = {
    ...(body.boxOpenTickets !== undefined ? { boxOpenTickets: body.boxOpenTickets } : {}),
  };
  BB_LOTTERY_TICKET_TIER_GROUPS.forEach((key) => {
    const tiers = normalizeTiers(body[key]);
    if (tiers !== undefined) payload[key] = tiers;
  });

  const { data } = await axios.post<any>(`${prefix}/lottery-ticket-ladder`, payload, {
    headers: h,
  });
  return unwrapOrThrow<BbLotteryTicketLadder>(data);
}

// =============================================================================
// 4. 内容
// =============================================================================

export async function bbGetTasks(): Promise<BbTask[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/tasks`, { headers: h });
  return unwrapOrThrow<BbTask[]>(data);
}

export async function bbCreateTask(body: Record<string, unknown>): Promise<BbTask> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/tasks`, body, { headers: h });
  return unwrapOrThrow<BbTask>(data);
}

export async function bbUpdateTask(id: number, body: Record<string, unknown>): Promise<BbTask> {
  const h = await getAuthHeaders();
  const { data } = await axios.put<any>(`${prefix}/tasks/${id}`, body, { headers: h });
  return unwrapOrThrow<BbTask>(data);
}

export async function bbDeleteTask(id: number): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.delete<any>(`${prefix}/tasks/${id}`, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

export async function bbGetWinnersTicker(): Promise<BbWinnersTickerRow[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/winners-ticker`, { headers: h });
  return unwrapOrThrow<BbWinnersTickerRow[]>(data);
}

export async function bbUpsertWinnersTicker(
  body: Record<string, unknown>
): Promise<BbWinnersTickerRow> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/winners-ticker`, body, { headers: h });
  return unwrapOrThrow<BbWinnersTickerRow>(data);
}

export async function bbDeleteWinnersTicker(id: number): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.delete<any>(`${prefix}/winners-ticker/${id}`, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

export async function bbGetWinRecords(params?: {
  source?: string;
  status?: string;
  rewardCategory?: string;
  walletAddress?: string;
  userId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<BbWinRecordsPage> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/win-records`, { headers: h, params });
  return unwrapOrThrow<BbWinRecordsPage>(data);
}

export async function bbGetPendingWinReviews(
  limit?: number,
  offset?: number
): Promise<BbWinRecordsPage> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/win-reviews/pending`, {
    headers: h,
    params: { limit, offset },
  });
  return unwrapOrThrow<BbWinRecordsPage>(data);
}

export async function bbApproveWin(winId: string): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/wins/${winId}/approve`, {}, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

export async function bbRejectWin(winId: string, note: string): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/wins/${winId}/reject`,
    { note },
    { headers: h }
  );
  return unwrapOrThrow<boolean>(data);
}

export async function bbGetOpenSessions(params?: {
  userId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<BbPagedResponse<BbOpenSessionEntry>> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/open-sessions`, { headers: h, params });
  return unwrapOrThrow<BbPagedResponse<BbOpenSessionEntry>>(data);
}

export async function bbGetTaskClaims(params?: {
  userId?: string;
  taskId?: number;
  status?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<BbPagedResponse<BbTaskClaimEntry>> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/task-claims`, { headers: h, params });
  return unwrapOrThrow<BbPagedResponse<BbTaskClaimEntry>>(data);
}

export async function bbGetLotteryTickets(params?: {
  userId?: string;
  issueId?: number;
  limit?: number;
  offset?: number;
}): Promise<BbPagedResponse<BbLotteryTicketEntry>> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/lottery-tickets`, { headers: h, params });
  return unwrapOrThrow<BbPagedResponse<BbLotteryTicketEntry>>(data);
}

export async function bbGetUserXpHistory(params?: {
  userId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<BbPagedResponse<BbUserXpHistoryEntry>> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/user-xp-history`, { headers: h, params });
  return unwrapOrThrow<BbPagedResponse<BbUserXpHistoryEntry>>(data);
}

export async function bbGetSpinBoxGrantHistory(params?: {
  userId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<BbPagedResponse<BbSpinBoxGrantHistoryEntry>> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/spin-box-grant-history`, { headers: h, params });
  return unwrapOrThrow<BbPagedResponse<BbSpinBoxGrantHistoryEntry>>(data);
}

function parseFilenameFromDisposition(disposition: string | null) {
  if (!disposition) return null;
  const m = disposition.match(/filename\s*=\s*"([^"]+)"/i);
  return m?.[1] ?? null;
}

/** 导出 USDC WIN 流水 CSV（与 POST reward-feed-usdc 筛选一致） */
export async function bbExportUsdCRewardFeedCsv(
  body: Record<string, unknown>
): Promise<{ blob: Blob; filename: string }> {
  const url = `${prefix}/reward-feed-usdc/export-csv`;
  const headers = await getFetchAuthHeaders({
    Accept: 'text/csv,*/*',
    'Content-Type': 'application/json',
  });
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const j = await res.json();
        msg = (j as any)?.message ?? msg;
      } else {
        const t = await res.text();
        if (t) msg = t;
      }
    } catch {
      // ignore
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  const filename =
    parseFilenameFromDisposition(res.headers.get('content-disposition')) ||
    `blind-box-reward-feed-usdc-${Date.now()}.csv`;

  return { blob, filename };
}

export async function bbGetUsdCRewardFeed(body?: {
  userId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<BbUsdcRewardFeedResponse> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/reward-feed-usdc`, body || {}, { headers: h });
  return unwrapOrThrow<BbUsdcRewardFeedResponse>(data);
}

export async function bbGetRewardFeed(body?: {
  userId?: string;
  walletAddress?: string;
  feedTypes?: string[];
  sources?: string[];
  /** 筛选 WIN 行状态（GraphQL 参数 winStatus；条目字段仍为 status） */
  winStatus?: string;
  rewardCategory?: string;
  seasonId?: string;
  issueId?: number | string;
  issueNumber?: string;
  taskSlug?: string;
  from?: string;
  to?: string;
  /** true=时间从旧到新；默认 false / 不传=从新到旧 */
  sortAsc?: boolean;
  limit?: number;
  offset?: number;
}): Promise<BbRewardFeedResponse> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/reward-feed`, body || {}, { headers: h });
  return unwrapOrThrow<BbRewardFeedResponse>(data);
}

export async function bbGetUserRewardSummary(userId: string): Promise<BbUserRewardSummary> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/user-reward-summary`, {
    headers: h,
    params: { userId },
  });
  return unwrapOrThrow<BbUserRewardSummary>(data);
}

// =============================================================================
// 5. 提现管理
// =============================================================================

export async function bbGetWithdrawals(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<BbWithdrawalsPage> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/withdrawals`, { headers: h, params });
  return unwrapOrThrow<BbWithdrawalsPage>(data);
}

export async function bbGetPendingWithdrawals(
  limit?: number,
  offset?: number
): Promise<BbWithdrawalsPage> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/withdrawals/pending`, {
    headers: h,
    params: { limit, offset },
  });
  return unwrapOrThrow<BbWithdrawalsPage>(data);
}

/** 批准提现（后端对应 Loyalty 仅 withdrawalId，链上由 Loyalty 执行） */
export async function bbProcessWithdrawal(withdrawalId: string): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/withdrawals/${withdrawalId}/process`, {}, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

export async function bbRejectWithdrawal(withdrawalId: string, reason: string): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/withdrawals/${withdrawalId}/reject`,
    { reason },
    { headers: h },
  );
  return unwrapOrThrow<boolean>(data);
}

/** GET /blind-box/social-followers — 对应 GraphQL bbAdminSocialFollowers */
export async function bbGetSocialFollowers(params?: {
  types?: BbSocialFollowType[] | string[];
  type?: BbSocialFollowType | string;
  isFollowing?: boolean;
  userId?: string;
  walletAddress?: string;
  socialUsername?: string;
  limit?: number;
  offset?: number;
}): Promise<BbAdminSocialFollowListResponse> {
  const h = await getAuthHeaders();
  const search = new URLSearchParams();
  const p = params || {};
  const typesList = p.types?.length
    ? p.types
    : p.type !== undefined && String(p.type).trim() !== ''
      ? [String(p.type).trim()]
      : undefined;
  if (typesList?.length) {
    typesList.forEach((t) => {
      const s = String(t).trim();
      if (s) search.append('types', s);
    });
  }
  if (p.userId !== undefined && String(p.userId).trim() !== '')
    search.set('userId', String(p.userId).trim());
  if (p.walletAddress !== undefined && String(p.walletAddress).trim() !== '')
    search.set('walletAddress', String(p.walletAddress).trim());
  if (p.socialUsername !== undefined && String(p.socialUsername).trim() !== '')
    search.set('socialUsername', String(p.socialUsername).trim());
  if (p.isFollowing === true) search.set('isFollowing', 'true');
  if (p.isFollowing === false) search.set('isFollowing', 'false');
  if (p.limit !== undefined) search.set('limit', String(p.limit));
  if (p.offset !== undefined) search.set('offset', String(p.offset));
  const q = search.toString();
  const { data } = await axios.get<any>(`${prefix}/social-followers${q ? `?${q}` : ''}`, {
    headers: h,
  });
  return unwrapOrThrow<BbAdminSocialFollowListResponse>(data);
}

/** POST /blind-box/social-followers/verify — 对应 GraphQL bbAdminVerifySocialFollow（userId 与 walletAddress 至少一项） */
export async function bbVerifySocialFollow(body: {
  userId?: string;
  walletAddress?: string;
  type: BbSocialFollowType | string; // string：兼容接口返回未知枚举
}): Promise<BbAdminSocialFollowEntry> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/social-followers/verify`, body, { headers: h });
  return unwrapOrThrow<BbAdminSocialFollowEntry>(data);
}

// =============================================================================
// Leaderboard: one site-wide Phase timeline + a per-phase set of Boards
// =============================================================================
// Model:
//   - Phase: one shared timeline; only one phase is active at a time (overlap rejected on save).
//   - Board: a (source, metric) pair added PER PHASE (not a fixed global catalog). Each phase starts
//     with zero boards; admin adds boards from the catalog allow-list, or clones a prior phase's set.
//     A board carries `enabled` — a per-phase hide switch that drops it from public reads, the recorder,
//     and settlement while off. Identified by `phaseBoardId`.
//   - Board catalog: the fixed (source, metric) allow-list an admin may add to a phase.
//   - Reward tier: per board (phaseBoardId), rank-band rewards. Dual-currency per band — each band
//     carries usdcRewardPerRank + boxRewardPerRank (either "0" to skip), plus autoPayout for both legs.
//     There is no board-level rewardKind; the BOX/USDC enum only tags per-currency payout entries.
//   - Config lock: tier edits require the owning phase to be `upcoming`; phase timing follows
//     upcoming=full / active=endTime-only / completed=locked. Board membership + attribute edits
//     (label/enabled/order) are NOT phase-locked; a board can only be removed if it has no
//     tiers/stats/payout data (disable it instead).

/** Board sources; VOLUME only for the first four, PNL for all five (9 boards total). */
export const BB_LB_SOURCES = [
  'FUTURES',
  'PREDICTION_SPOT',
  'PREDICTION_LEVERAGE',
  'PREDICTION_SPOT_LEVERAGE',
  'TAPGRID',
] as const;
export type BbLbSource = (typeof BB_LB_SOURCES)[number];

export const BB_LB_METRICS = ['VOLUME', 'PNL'] as const;
export type BbLbMetric = (typeof BB_LB_METRICS)[number];

/** Reward kind (payout entries only): BOX = welfare blind-box count; USDC = USDC amount. */
export const BB_LB_REWARD_KINDS = ['BOX', 'USDC'] as const;
export type BbLbRewardKind = (typeof BB_LB_REWARD_KINDS)[number];

/** Phase (BBAdminLeaderboardPhase); upcoming→active→completed is flipped automatically by a scheduler. */
export interface BbLbPhase {
  id: string;
  name: string;
  startTime: string | number;
  endTime: string | number;
  status: string;
  /** When the phase ended and its payout ledger was frozen; null = not yet frozen. */
  frozenAt?: string | number | null;
  createdAt?: string | number;
  updatedAt?: string | number;
}

/** A board configured for one phase (BBAdminPhaseBoard). Identified by `id` = phaseBoardId. */
export interface BbLbPhaseBoard {
  id: string;
  phaseId: string;
  source: BbLbSource;
  metric: BbLbMetric;
  label: string;
  /** Per-phase hide switch; false → dropped from public reads, recorder, and settlement. */
  enabled: boolean;
  displayOrder: number;
  /** Reward tiers configured for this board; 0 = pays no one at settlement. */
  tierCount?: number;
}

/** An entry in the fixed (source, metric) allow-list an admin may add to a phase (BBBoardCatalogEntry). */
export interface BbLbBoardCatalogEntry {
  source: BbLbSource;
  metric: BbLbMetric;
  defaultLabel: string;
}

/**
 * A board's reward tier (BBAdminLeaderboardPhaseTier). Dual-currency per rank: a band may pay USDC,
 * boxes, or both — either leg is `"0"` to skip that currency.
 */
export interface BbLbPhaseTier {
  id: string;
  phaseBoardId: string;
  minRank: number;
  maxRank: number;
  /** Decimal string; USDC per rank ("0" = no USDC leg). */
  usdcRewardPerRank: string;
  /** Decimal string; welfare-box count per rank ("0" = no box leg). */
  boxRewardPerRank: string;
  /** Applies to both legs. */
  autoPayout: boolean;
}

/** Input for a whole-set tier replace (BBLeaderboardPhaseTierInput). */
export interface BbLbPhaseTierInput {
  minRank: number;
  maxRank: number;
  usdcRewardPerRank: string;
  boxRewardPerRank: string;
  autoPayout: boolean;
}

/** One ranked row within an export board (BBLeaderboardRankingExportRow). */
export interface BbLbRankingExportRow {
  rank: number;
  userId?: string | null;
  walletAddress?: string | null;
  value: string | number;
  /** Frozen USDC payout if that entry exists, else tier-projected; PnL ≤ 0 → 0. */
  usdcRewardAmount: string | number;
  /** Frozen box payout if that entry exists, else tier-projected; PnL ≤ 0 → 0. */
  boxRewardAmount: string | number;
  /** pending | completed | failed, or null if not yet frozen (reflects the BOX entry when both exist). */
  rewardStatus?: string | null;
  /** When the ranked stat was first recorded, or null. */
  achievedAt?: string | number | null;
  /** Live: the user is currently banned from Blind Box (their payout is held). */
  isBanned?: boolean;
}

/** One board's sheet in a ranking export (BBLeaderboardRankingExportBoard). */
export interface BbLbRankingExportBoard {
  source: BbLbSource;
  metric: BbLbMetric;
  label: string;
  rowCount: number;
  rows: BbLbRankingExportRow[];
}

/** Grouped ranking export for a phase (BBLeaderboardRankingExport). */
export interface BbLbRankingExport {
  phaseId: string;
  phaseName: string;
  phaseStartTime?: string | number | null;
  phaseEndTime?: string | number | null;
  /** One entry per board; only boards with ≥1 ranked row are included. */
  boards: BbLbRankingExportBoard[];
}

/** A single payout entry (BBPhaseSettlementEntry) — one row per currency (a winner with both appears twice). */
export interface BbLbPayoutEntry {
  id: string;
  rank: number;
  userId: string;
  walletAddress?: string | null;
  /** The ranked stat that earned this entry (VOLUME or PNL). */
  value: string | number;
  /** Boxes or USDC, per this entry's rewardKind. */
  rewardAmount: string | number;
  rewardKind: BbLbRewardKind;
  /** pending | completed | failed */
  status: string;
  distributedAt?: string | number | null;
  failReason?: string | null;
  /** Live: the user is currently banned from Blind Box — this entry is held (stays pending, all pay
   *  paths skip it). Both of a winner's entries share the same live status. */
  isBanned?: boolean;
}

/** A board's settlement view (BBPhaseSettlement). */
export interface BbLbPhaseSettlement {
  phaseBoardId: string;
  /** Number of ranked users. */
  onListCount: number;
  /** Number already distributed. */
  /** Completed entries across both currencies. */
  distributedCount: number;
  /** USDC owed (Σ over all USDC entries) and already sent (Σ over completed USDC entries). */
  totalUsdcToSend: string | number;
  sentUsdc: string | number;
  /** Boxes owed (Σ over all BOX entries) and already sent (Σ over completed BOX entries). */
  totalBoxToSend: string | number;
  sentBox: string | number;
  /** false = live preview (projected entries, not distributable — hide Distribute); true = real frozen
   *  ledger. Absent on older responses → treat as frozen. */
  isFrozen?: boolean;
  /** When AUTO payout releases (endTime + cooling-off); null while paused/unresolved. Informational —
   *  admin distribution is never gated by it. */
  payoutEligibleAt?: string | number | null;
  /** One row per currency; a winner earning both appears as two entries. */
  entries: BbLbPayoutEntry[];
}

/** Bulk-pay result (BBPhaseBulkPayResult). */
export interface BbLbBulkPayResult {
  paid: number;
  failed: number;
}

// ── Tab 1: Phase management ───────────────────────────────────────────────────

/** GET /blind-box/leaderboard/phases — phase list (newest first). */
export async function bbLbGetPhases(): Promise<BbLbPhase[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/leaderboard/phases`, { headers: h });
  return unwrapOrThrow<BbLbPhase[]>(data);
}

/** POST /blind-box/leaderboard/phases — create a phase (backend checks overlap / end>start). */
export async function bbLbCreatePhase(body: {
  name: string;
  startTime: string | number;
  endTime: string | number;
}): Promise<BbLbPhase> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/leaderboard/phases`, body, { headers: h });
  return unwrapOrThrow<BbLbPhase>(data);
}

/** PUT /blind-box/leaderboard/phases/:id — partial update (upcoming only). */
export async function bbLbUpdatePhase(
  id: string,
  body: { name?: string; startTime?: string | number; endTime?: string | number }
): Promise<BbLbPhase> {
  const h = await getAuthHeaders();
  const { data } = await axios.put<any>(`${prefix}/leaderboard/phases/${id}`, body, { headers: h });
  return unwrapOrThrow<BbLbPhase>(data);
}

/** DELETE /blind-box/leaderboard/phases/:id — delete (upcoming only). */
export async function bbLbDeletePhase(id: string): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.delete<any>(`${prefix}/leaderboard/phases/${id}`, { headers: h });
  return unwrapOrThrow<boolean>(data);
}

/**
 * GET /blind-box/leaderboard/phases/:phaseId/export-ranking — final ranking grouped per board.
 * Omit phaseBoardId for ALL boards; provide it to export a single board.
 */
export async function bbLbExportRanking(
  phaseId: string,
  phaseBoardId?: string
): Promise<BbLbRankingExport> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/leaderboard/phases/${phaseId}/export-ranking`, {
    headers: h,
    params: phaseBoardId ? { phaseBoardId } : undefined,
  });
  return unwrapOrThrow<BbLbRankingExport>(data);
}

// ── Tab 2: Reward configuration (per-phase boards + tiers) ────────────────────

/** GET /blind-box/leaderboard/board-catalog — the fixed (source, metric) allow-list. */
export async function bbLbGetBoardCatalog(): Promise<BbLbBoardCatalogEntry[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/leaderboard/board-catalog`, { headers: h });
  return unwrapOrThrow<BbLbBoardCatalogEntry[]>(data);
}

/** GET /blind-box/leaderboard/phases/:phaseId/boards — a phase's boards (may be empty), by displayOrder. */
export async function bbLbGetPhaseBoards(phaseId: string): Promise<BbLbPhaseBoard[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/leaderboard/phases/${phaseId}/boards`, {
    headers: h,
  });
  return unwrapOrThrow<BbLbPhaseBoard[]>(data);
}

/** POST /blind-box/leaderboard/phases/:phaseId/boards — add a board to a phase (not phase-locked). */
export async function bbLbAddPhaseBoard(
  phaseId: string,
  body: {
    source: BbLbSource;
    metric: BbLbMetric;
    label?: string;
    enabled?: boolean;
    displayOrder?: number;
  }
): Promise<BbLbPhaseBoard> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/leaderboard/phases/${phaseId}/boards`, body, {
    headers: h,
  });
  return unwrapOrThrow<BbLbPhaseBoard>(data);
}

/** PUT /blind-box/leaderboard/phase-boards/:phaseBoardId — edit a board (incl. enabled; not phase-locked). */
export async function bbLbUpdatePhaseBoard(
  phaseBoardId: string,
  body: {
    label?: string;
    enabled?: boolean;
    displayOrder?: number;
  }
): Promise<BbLbPhaseBoard> {
  const h = await getAuthHeaders();
  const { data } = await axios.put<any>(`${prefix}/leaderboard/phase-boards/${phaseBoardId}`, body, {
    headers: h,
  });
  return unwrapOrThrow<BbLbPhaseBoard>(data);
}

/** DELETE /blind-box/leaderboard/phase-boards/:phaseBoardId — remove a board (rejected if it has data). */
export async function bbLbRemovePhaseBoard(phaseBoardId: string): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.delete<any>(`${prefix}/leaderboard/phase-boards/${phaseBoardId}`, {
    headers: h,
  });
  return unwrapOrThrow<boolean>(data);
}

/** POST /blind-box/leaderboard/phase-boards/clone — clone a phase's whole board set (+ tiers) onto an empty upcoming phase. */
export async function bbLbClonePhaseBoards(body: {
  fromPhaseId: string;
  toPhaseId: string;
}): Promise<BbLbPhaseBoard[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/leaderboard/phase-boards/clone`, body, {
    headers: h,
  });
  return unwrapOrThrow<BbLbPhaseBoard[]>(data);
}

/** GET /blind-box/leaderboard/phase-boards/:phaseBoardId/tiers — a board's tiers. */
export async function bbLbGetPhaseTiers(phaseBoardId: string): Promise<BbLbPhaseTier[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(
    `${prefix}/leaderboard/phase-boards/${phaseBoardId}/tiers`,
    { headers: h }
  );
  return unwrapOrThrow<BbLbPhaseTier[]>(data);
}

/** PUT /blind-box/leaderboard/phase-boards/:phaseBoardId/tiers — whole-set replace (empty array clears; phase must be upcoming). */
export async function bbLbReplacePhaseTiers(
  phaseBoardId: string,
  tiers: BbLbPhaseTierInput[]
): Promise<BbLbPhaseTier[]> {
  const h = await getAuthHeaders();
  const { data } = await axios.put<any>(
    `${prefix}/leaderboard/phase-boards/${phaseBoardId}/tiers`,
    { tiers },
    { headers: h }
  );
  return unwrapOrThrow<BbLbPhaseTier[]>(data);
}

/** POST .../phase-boards/:phaseBoardId/tiers/apply-to-all — copy this board's tiers onto every other board of the same phase (upcoming only). */
export async function bbLbApplyTiersToAll(phaseBoardId: string): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/leaderboard/phase-boards/${phaseBoardId}/tiers/apply-to-all`,
    {},
    { headers: h }
  );
  return unwrapOrThrow<boolean>(data);
}

// ── Tab 3: Settlement & payout ────────────────────────────────────────────────

/** GET /blind-box/leaderboard/phase-boards/:phaseBoardId/settlement — settlement view. */
export async function bbLbGetSettlement(phaseBoardId: string): Promise<BbLbPhaseSettlement> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(
    `${prefix}/leaderboard/phase-boards/${phaseBoardId}/settlement`,
    { headers: h }
  );
  return unwrapOrThrow<BbLbPhaseSettlement>(data);
}

/** POST .../phase-boards/:phaseBoardId/payout/bulk-pay — distribute all unpaid (pending|failed) entries in one click. */
export async function bbLbBulkPay(phaseBoardId: string): Promise<BbLbBulkPayResult> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/leaderboard/phase-boards/${phaseBoardId}/payout/bulk-pay`,
    {},
    { headers: h }
  );
  return unwrapOrThrow<BbLbBulkPayResult>(data);
}

/** POST /blind-box/leaderboard/payout-entries/:entryId/pay — distribute one entry. */
export async function bbLbPayEntry(entryId: string): Promise<BbLbPayoutEntry> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/leaderboard/payout-entries/${entryId}/pay`,
    {},
    { headers: h }
  );
  return unwrapOrThrow<BbLbPayoutEntry>(data);
}

/** POST /blind-box/leaderboard/payout-entries/:entryId/retry — retry a failed entry. */
export async function bbLbRetryEntry(entryId: string): Promise<BbLbPayoutEntry> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(
    `${prefix}/leaderboard/payout-entries/${entryId}/retry`,
    {},
    { headers: h }
  );
  return unwrapOrThrow<BbLbPayoutEntry>(data);
}

// ── Payout replace: request -> approval by a different admin (XBIT-4530) ────────
// The former PUT payout-entries/:entryId (edit + pay in one call) is gone. APPROVING a replace re-distributes
// the entry, so it pays the entry again, to the new wallet, when it was not completed.

/** POST .../payout-entries/:entryId/replace-requests — creates a PENDING request; nothing is sent upstream. */
export async function bbLbRequestReplace(
  entryId: string,
  body: { walletAddress?: string; rewardAmount?: string; reason?: string }
): Promise<MoneyRequest> {
  return moneyCall<MoneyRequest>(
    'POST',
    `blind-box/leaderboard/payout-entries/${entryId}/replace-requests`,
    body
  );
}

/** GET .../payout-replace-requests — needs bb-leaderboard-payout:edit or :approve. */
export async function bbLbListReplaceRequests(params?: {
  page?: number;
  size?: number;
  status?: string;
}): Promise<MoneyRequestPage> {
  return moneyCall<MoneyRequestPage>(
    'GET',
    'blind-box/leaderboard/payout-replace-requests',
    undefined,
    params
  );
}

/** POST .../payout-replace-requests/:id/approve — a different admin; calls the upstream once. */
export async function bbLbApproveReplace(id: string): Promise<MoneyRequest> {
  return moneyCall<MoneyRequest>(
    'POST',
    `blind-box/leaderboard/payout-replace-requests/${id}/approve`,
    {}
  );
}

/** POST .../payout-replace-requests/:id/reject */
export async function bbLbRejectReplace(id: string, reason: string): Promise<MoneyRequest> {
  return moneyCall<MoneyRequest>(
    'POST',
    `blind-box/leaderboard/payout-replace-requests/${id}/reject`,
    { reason }
  );
}

/** POST .../payout-replace-requests/:id/cancel — the requester only. */
export async function bbLbCancelReplace(id: string): Promise<MoneyRequest> {
  return moneyCall<MoneyRequest>(
    'POST',
    `blind-box/leaderboard/payout-replace-requests/${id}/cancel`,
    {}
  );
}

/** Ban record returned by POST /blind-box/ban-user. */
export interface BbLbBanRecord {
  userId?: string;
  isBanned: boolean;
  reason?: string | null;
  bannedBy?: string | null;
  bannedAt?: string | number | null;
}

/**
 * POST /blind-box/ban-user — ban/unban a user (`bb-leaderboard:update`). While banned, the user
 * cannot open blind boxes, claim task rewards or withdraw (withdrawal approval is refused), and
 * their leaderboard payout entries are held: every distribution path skips them (they stay
 * `pending`). Unbanning does NOT auto-release held payouts — they must be distributed manually.
 * Exactly one of `userId` / `walletAddress` is expected. `operator` is accepted for backwards
 * compatibility, but the server records the operator from the JWT.
 */
export async function bbLbBanUser(body: {
  userId?: string;
  walletAddress?: string;
  banned: boolean;
  operator?: string;
  reason?: string;
}): Promise<BbLbBanRecord> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/ban-user`, body, { headers: h });
  return unwrapOrThrow<BbLbBanRecord>(data);
}

/** One row of GET /blind-box/banned-users */
export interface BbBannedUser {
  userId: string;
  isBanned: boolean;
  reason?: string | null;
  bannedBy?: string | null;
  bannedAt?: string | number | null;
  unbannedBy?: string | null;
  unbannedAt?: string | number | null;
}

export interface BbBannedUsersPage {
  items: BbBannedUser[];
  total: number;
  page: number;
  pageSize: number;
}

/** GET /blind-box/banned-users — ban records (`bb-leaderboard:read`).
 *  activeOnly=true returns currently-banned users only; page is 1-based. */
export async function bbGetBannedUsers(params: {
  page?: number;
  pageSize?: number;
  activeOnly?: boolean;
}): Promise<BbBannedUsersPage> {
  const h = await getAuthHeaders();
  const { data } = await axios.get<any>(`${prefix}/banned-users`, { headers: h, params });
  return unwrapOrThrow<BbBannedUsersPage>(data);
}

/** The whole leaderboard module sits behind this feature flag (seeds `false` — switch on per env).
 *  Toggled via the shared `bbGetFeatureFlags` / `bbSetFeatureFlag` above. */
export const BB_LEADERBOARD_FLAG = 'leaderboard_enabled';

// =============================================================================
// 工具
// =============================================================================

export async function bbResetToDefault(): Promise<boolean> {
  const h = await getAuthHeaders();
  const { data } = await axios.post<any>(`${prefix}/reset-to-default`, {}, { headers: h });
  return unwrapOrThrow<boolean>(data);
}
